import dayjs from 'common/dayjs.ts';

export const now = () => new Date().toISOString();

export const dateToInput = (value: string) => (value ? dayjs(value).format('YYYY-MM-DD') : '');
export const datetimeToInput = (value: string) => (value ? dayjs(value).format('YYYY-MM-DDTHH:mm') : '');

// Mon 17th 24
export const dateToLocalYMD = (value: null | string | Date, placeholder = '-') => (value ? dayjs(value).format('ll') : placeholder);
// Mon 17th 24, 5:30pm
export const dateToLocalYMDHM = (value: null | string | Date, placeholder = '-') => (value ? dayjs(new Date(value)).format('YYYY-MM-DD HH:mm') : placeholder);
export const dateToTimezonedFormated = (value: null | string | Date, timezone: string, placeholder = '-', overrideDateFormat = 'YYYY-MM-DD HH:mm') => {
  return value ? dayjs.tz(new Date(value), timezone).format(overrideDateFormat) : placeholder;
};
// YYYY-MM-DD
export const dateToYMD = (value: null | string | Date, placeholder = '-') => (value ? dayjs(new Date(value)).format('YYYY-MM-DD') : placeholder);
// YYYY-MM-DD HH
export const dateToYMDH = (value: null | string | Date, placeholder = '-') => (value ? dayjs(value).format('YYYY-MM-DD HH') : placeholder);
// YYYY-MM-DD HH:MM
export const dateToYMDHM = (value: null | string | Date, placeholder = '-') => (value ? dayjs(value).format('YYYY-MM-DD HH:mm') : placeholder);
// YYYY-MM-DD HH:MM:SS
export const dateToYMDHMS = (value: null | string | Date, placeholder = '-') => (value ? dayjs(value).format('YYYY-MM-DD HH:mm:ss') : placeholder);
// HH:MM
export const dateToHM = (value: null | string | Date, placeholder = '-') => (value ? dayjs(value).format('HH:mm') : placeholder);
// DD/MM
export const dateToDM = (value: null | string | Date, placeholder = '-') => (value ? dayjs(value).format('DD/MM') : placeholder);
// lll - short date + time with month name, e.g. "Sep 4, 1986 8:30 PM"
export const dateToLLL = (value: null | string | Date, placeholder = '-') => (value ? dayjs(value).format('lll') : placeholder);

export const dateToInputValue = (value: null | string | Date, timezone?: string) => (value ? dayjs(value).tz(timezone).format('YYYY-MM-DDTHH:mm') : '');

export const dateRangeToTimezonedYMHM = (from: null | string | Date, to: null | string | Date, timezone: string, dateFormat = 'YYYY-MM-DD HH:mm', placeholder = '-') => {
  if (!from || !to) return placeholder;
  const fromDate = dayjs.tz(new Date(from), timezone);
  const toDate = dayjs.tz(new Date(to), timezone);

  // If days are different, display: datetime1 - datetime2
  if (fromDate.format('YYYY-MM-DD') !== toDate.format('YYYY-MM-DD')) {
    return `${fromDate.format(dateFormat)} - ${toDate.format(dateFormat)}`;
  }

  // Else, display: datetime1 - time2 (24-hour, same day so only the end time is shown)
  return `${fromDate.format(dateFormat)} - ${toDate.format('HH:mm')}`;
};

export const getLocalTimezone = () => dayjs.tz.guess();

// convert seconds amount to "1d 2h 50min 30sec" string
export const secondsToTimeSpan = (
  seconds: number,
  options?: {
    daySuffix?: string;
    hourSuffix?: string;
    minuteSuffix?: string;
    secondSuffix?: string;
    separator?: string;
    pad?: boolean;
  },
): string => {
  const daySuffix = options?.daySuffix ?? 'd';
  const hourSuffix = options?.hourSuffix ?? 'h';
  const minuteSuffix = options?.minuteSuffix ?? 'min';
  const secondSuffix = options?.secondSuffix ?? 'sec';
  const separator = options?.separator ?? ' ';
  const pad = options?.pad ?? false;
  const fmt = (n: number) => (pad ? String(n).padStart(2, '0') : String(n));

  const d = Math.floor(seconds / (24 * 60 * 60));
  seconds -= d * (24 * 60 * 60);
  const h = Math.floor(seconds / (60 * 60));
  seconds -= h * (60 * 60);
  const m = Math.floor(seconds / 60);
  seconds -= m * 60;
  const s = Math.floor(seconds);

  const dDisplay = d > 0 ? `${fmt(d)}${daySuffix}` : '';
  const hDisplay = h > 0 ? `${fmt(h)}${hourSuffix}` : '';
  const mDisplay = m > 0 ? `${fmt(m)}${minuteSuffix}` : '';
  const sDisplay = s > 0 ? `${fmt(s)}${secondSuffix}` : '';

  const time_span = `${dDisplay}${separator}${hDisplay}${separator}${mDisplay}${separator}${sDisplay}`.trim().replaceAll(/\s+/g, ' ');
  return time_span;
};

/**
 * Estimates the seconds remaining for any progress-tracked operation, from its start time and
 * processed/total counts. Uses the cumulative average rate since `startedAt` (stable — avoids the
 * per-tick jitter of an instantaneous rate). Returns `null` when there isn't enough signal to
 * estimate yet (not started, zero total, or nothing processed).
 *
 * Pair with {@link secondsToTimeSpan} to render, e.g. `secondsToTimeSpan(estimateEtaSeconds(startedAt, processed, total))`.
 *
 * @param now - injectable current epoch ms (defaults to `Date.now()`), for testability.
 */
export const estimateEtaSeconds = (startedAt: string | Date | null, processed: number, total: number, now: number = Date.now()): number | null => {
  if (!startedAt || total <= 0 || processed <= 0) return null;
  const elapsedSeconds = (now - new Date(startedAt).getTime()) / 1000;
  if (elapsedSeconds <= 0) return null;
  const ratePerSecond = processed / elapsedSeconds;
  if (ratePerSecond <= 0) return null;
  return Math.ceil((total - processed) / ratePerSecond);
};

/**
 * Formats a date to a human readable format
 * @param date: The date to be formatted
 * @param pastHoursConsideredRecent: The amount of hours in the past that we consider the date to be recent
 * @returns the formatted date and a boolean indicating if the date is recent
 */
export const formatDateFrom = (date: string | Date, pastHoursConsideredRecent = 24) => {
  const absoluteDate = dateToYMDHMS(new Date(date));
  const fromNow = dayjs(new Date(date)).fromNow();
  const isRecent = Date.now() - Number(new Date(date)) < 3600 * pastHoursConsideredRecent * 1000;

  const formattedDate = isRecent ? fromNow : absoluteDate;

  return {
    isRecent,
    formattedDate,
  };
};
